import { createContext } from 'react'

import type { LoginInput, SignupInput } from '@/lib/api/endpoints/auth'
import type { User } from '@/types/api'

/**
 * Claims decodificados do JWT do Supabase Auth (`access_token`).
 *
 * Por que tipar campo a campo em vez de `Record<string, unknown>`: o painel
 * de visualização (`AuthDebugBadge`) consome direto e queremos autocomplete
 * + erro de typo em tempo de compilação. O `app_metadata.vectraclip` é onde
 * vivem os claims customizados (company_id, role) que alimentam as RLS
 * policies do schema `vectraclip` no Supabase — ver migration
 * `vec_194_auth_real_setup`.
 */
export interface DecodedJwtClaims {
  /** Issuer — ex.: `https://<ref>.supabase.co/auth/v1`. */
  iss?: string
  /** Subject — `auth.users.id` do Supabase. */
  sub?: string
  /** Audience — sempre `authenticated` em sessão de user logado. */
  aud?: string
  /** Expiração em segundos epoch. */
  exp?: number
  /** Issued at em segundos epoch. */
  iat?: number
  email?: string
  /** Role no Postgres (sempre `authenticated` para sessão de user). */
  role?: string
  /** Authenticator Assurance Level — `aal1` = senha, `aal2` = MFA. */
  aal?: string
  session_id?: string
  is_anonymous?: boolean
  app_metadata?: {
    provider?: string
    providers?: string[]
    /** Namespace dedicado ao VectraClip (não conflita com claims do CFN). */
    vectraclip?: {
      company_id?: string
      role?: 'admin' | 'operator' | 'viewer' | string
    }
  }
  user_metadata?: Record<string, unknown>
  /** Permite acesso a claims arbitrários sem cast. */
  [extraClaim: string]: unknown
}

export interface AuthContextValue {
  /** `true` enquanto a primeira validação do storage está acontecendo. */
  isHydrating: boolean
  /** `true` se há sessão válida E `getMe` confirmou. */
  isAuthenticated: boolean
  user: User | null
  /**
   * Access token bruto. Exposto para o `AuthDebugBadge` decodificar e exibir
   * claims, e para integrações futuras (ex.: subscriptions WebSocket que
   * precisam do JWT direto). NUNCA logar/persistir fora do `localStorage`
   * (cuidado em telemetria).
   */
  accessToken: string | null
  /** ISO 8601 — quando o `accessToken` expira. */
  expiresAt: string | null
  /**
   * Claims decodificados do header.payload do `accessToken`. Reativo: muda
   * sempre que há refresh. Útil pro painel de visualização e debug.
   * `null` se não há sessão ou se o decode falha (token malformado).
   */
  claims: DecodedJwtClaims | null
  /** Lança `ApiError` (status 401 = credencial errada). */
  login: (input: LoginInput) => Promise<void>
  /**
   * Self-service signup: cria user + company atomicamente e seta sessão.
   * Lança `ApiError` 403 se `SIGNUP_ENABLED=false` no backend.
   */
  signup: (input: SignupInput) => Promise<void>
  /** Best-effort: tenta avisar o servidor mas sempre limpa storage local. */
  logout: () => Promise<void>
  /**
   * Força um refresh manual do access token (rotaciona refresh token também).
   * Retorna `true` se sucesso, `false` se o refresh foi rejeitado (que vai
   * disparar o fluxo de logout via interceptor). Exposto pro debug panel.
   */
  refresh: () => Promise<boolean>
}

/**
 * Context separado do Provider para isolar declaração de tipos do componente
 * — assim `react-refresh/only-export-components` fica feliz e o HMR funciona
 * sem perder estado do React entre edições.
 */
export const AuthContext = createContext<AuthContextValue | null>(null)
